import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import api, { getApiImageUrl } from '../services/api';

const ModulesScreen = ({ currentPlayer }) => {
  const { t, language } = useLocalization();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [totalStars, setTotalStars] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPlayer && language) {
      checkPendingChoices();
      fetchModules();
    }
  }, [currentPlayer, language]);

  const checkPendingChoices = async () => {
    if (!currentPlayer) return;
    
    try {
      const response = await api.get('/api/choices/pending', {
        params: { player_id: currentPlayer.id }
      });
      
      if (response.status === 200) {
        const data = response.data;
        const choices = data.choices || [];
        
        // If there are pending choices, redirect to resolve them
        if (choices.length > 0) {
          const firstChoice = choices[0];
          console.log('Pending choice found:', firstChoice);
          
          // For dragon-selection, navigate to dragon selection screen  
          if (firstChoice.choice_type === 'dragon-selection') {
            navigate('/dragon-selection');
          }
          // Add more choice types here as needed
        }
      }
    } catch (error) {
      console.error('Error checking pending choices:', error);
    }
  };

  const fetchModules = async () => {
    if (!currentPlayer) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/modules', {
        params: { 
          player_id: currentPlayer.id,
          language: language || 'en'
        }
      });
      
      if (response.status === 200) {
        const data = response.data;
        console.log('Modules API response:', data);
        console.log('Modules data:', data.modules);
        setModules(data.modules || []);
        setTotalStars(data.total_stars || 0);
      } else {
        setError('Failed to load modules');
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const activeModules = modules.filter(m => m.status === 'active' || m.status === 'locked')
    .sort((a, b) => {
      // Active modules first, then locked modules
      if (a.status === 'active' && b.status === 'locked') return -1;
      if (a.status === 'locked' && b.status === 'active') return 1;
      return a.module_id.localeCompare(b.module_id); // Sort by module ID as fallback
    });
  const completedModules = modules.filter(m => m.status === 'completed');

  const handleModuleClick = (module) => {
    if (module.status === 'locked') {
      return; // Do nothing for locked modules
    }
    
    if (module.status === 'active') {
      // Take user to first card in module
      console.log(`Starting module: ${module.module_name} (${module.module_id})`);
      navigate(`/play/${module.module_id}`);
    } else if (module.status === 'completed') {
      // Take user to module review screen
      console.log(`Viewing module review: ${module.module_name} (${module.module_id})`);
      navigate(`/module-review/${module.module_id}`);
    }
  };

  const ModuleTile = ({ module }) => {
    const getStatusStyles = () => {
      switch (module.status) {
        case 'active':
          return 'bg-white hover:bg-blue-50 shadow-lg hover:shadow-xl border-blue-200 cursor-pointer transform hover:scale-105';
        case 'completed':
          return 'bg-green-50 hover:bg-green-100 shadow-lg hover:shadow-xl border-green-200 cursor-pointer transform hover:scale-105';
        case 'locked':
          return 'bg-gray-100 border-gray-300 opacity-60 cursor-not-allowed';
        default:
          return 'bg-white border-gray-200';
      }
    };

    const getProgressColor = () => {
      const progress = module.progress_percent || 0;
      if (progress >= 100) return 'bg-green-500';
      if (progress >= 75) return 'bg-blue-500';
      if (progress >= 50) return 'bg-yellow-500';
      return 'bg-orange-500';
    };

    return (
      <div 
        className={`module-card ${module.status === 'locked' ? 'locked' : ''} relative p-4 rounded-xl border-2 transition-all duration-300 ease-in-out ${getStatusStyles()}`}
        onClick={() => handleModuleClick(module)}
      >
        {/* Status Badge */}
        {module.status === 'completed' && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white text-xs font-bold">✓</span>
          </div>
        )}
        {module.status === 'locked' && (
          <div className="absolute bottom-2 right-2 w-7 h-7 bg-gray-500 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white text-sm">🔒</span>
          </div>
        )}

        {/* Module Icon - PNG image */}
        <div className="flex justify-center mb-3">
          <div className="w-16 h-16 relative">
            <img 
              src={getApiImageUrl(`/api/modules/${module.module_id}/icon`)}
              alt={module.module_name}
              className="w-full h-full object-contain"
              onError={(e) => {
                // Fallback to emoji if image fails to load
                console.log(`Failed to load image for module ${module.module_id}:`, e.target.src);
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
              onLoad={() => {
                console.log(`Successfully loaded image for module ${module.module_id}`);
              }}
            />
            <div className="text-5xl w-full h-full flex items-center justify-center" style={{display: 'none'}}>
              {module.icon || '📚'}
            </div>
          </div>
        </div>

        {/* Module Content */}
        <div className="text-center">
          <h3 className="text-xs md:text-base font-semibold text-gray-800 mb-2">{module.module_name}</h3>
          
          {module.status !== 'locked' && (
            <div className="space-y-1">
              {/* Progress Bar */}
              <div className="w-3/4 mx-auto bg-gray-200 rounded-full h-1 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ease-out ${getProgressColor()}`}
                  style={{ width: `${module.progress_percent || 0}%` }}
                />
              </div>
              <span className="text-xs font-medium text-gray-600">{Math.round(module.progress_percent || 0)}% {t('modules.complete')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Show no player selected state
  if (!currentPlayer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4 md:mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">{t('modules.title')}</h1>
            <p className="text-sm md:text-lg text-gray-600">{t('modules.subtitle')}</p>
          </div>
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="text-6xl mb-4">👥</div>
              <p className="text-gray-600">
                No player selected. Please select a player from your{' '}
                <Link to="/profile" className="text-blue-600 hover:text-blue-800 underline">
                  profile
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4 md:mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">{t('modules.title')}</h1>
            <p className="text-sm md:text-lg text-gray-600">{t('modules.subtitle')}</p>
          </div>
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4 md:mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">{t('modules.title')}</h1>
            <p className="text-sm md:text-lg text-gray-600">{t('modules.subtitle')}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">{t('modules.errorLoading')}</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchModules}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">{t('modules.title')}</h1>
          <p className="text-sm md:text-lg text-gray-600">{t('modules.subtitle')}</p>
        </div>

        {/* Available Modules Section */}
        <div className="mb-12">
          <div className="flex items-center mb-4 md:mb-8">
            <div className="flex-grow h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
            <h2 className="px-4 md:px-6 text-lg md:text-2xl font-semibold text-gray-700 bg-blue-50 rounded-full py-1 md:py-2">
              {t('modules.available')}
            </h2>
            <div className="flex-grow h-px bg-gradient-to-l from-transparent via-blue-300 to-transparent"></div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {activeModules.map(module => (
              <ModuleTile key={module.module_id} module={module} />
            ))}
          </div>
        </div>

        {/* Completed Modules Section */}
        {completedModules.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center mb-8">
              <div className="flex-grow h-px bg-gradient-to-r from-transparent via-green-300 to-transparent"></div>
              <h2 className="px-4 md:px-6 text-lg md:text-2xl font-semibold text-gray-700 bg-green-50 rounded-full py-1 md:py-2">
                {t('modules.completed')}
              </h2>
              <div className="flex-grow h-px bg-gradient-to-l from-transparent via-green-300 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {completedModules.map(module => (
                <ModuleTile key={module.module_id} module={module} />
              ))}
            </div>
          </div>
        )}

        {/* Total Stars Footer */}
        <div className="text-center mt-16 p-4 bg-white rounded-2xl shadow-lg">
          <div className="flex items-center justify-center space-x-2">
            <span className="text-lg font-bold text-yellow-600">
              {totalStars}
            </span>
            <span className="text-base">⭐</span>
            <span className="text-base font-medium text-gray-700">{t('modules.earnedSoFar')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesScreen;